import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
  Typography,
  Box,
  InputAdornment,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { coachingSessionAPI, memberProfileAPI } from '../../services/api';
import { CoachingSession, BookCoachingSessionData } from '../../types/coaching';
import { MemberProfile, UserRole } from '../../types';
import { parseDateOnlyString, formatTimeString } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';

interface BookCoachingSessionDialogProps {
  open: boolean;
  onClose: () => void;
  session: CoachingSession | null;
  onSuccess?: (message?: string) => void;
}

const BookCoachingSessionDialog: React.FC<BookCoachingSessionDialogProps> = ({
  open,
  onClose,
  session,
  onSuccess
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memberProfiles, setMemberProfiles] = useState<MemberProfile[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberProfile | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  
  const [formData, setFormData] = useState({
    startTime: new Date(),
    endTime: new Date(),
    memberNotes: '',
    isGuest: false,
    guestName: '',
    guestEmail: '',
    overrideCost: false,
    overriddenCost: 0
  });

  useEffect(() => {
    if (open && session) {
      // Parse session start time (format: "HH:MM:SS" or "HH:MM:SS+00")
      const sessionStartTimeParts = session.startTime.split(':');
      const sessionStartHours = parseInt(sessionStartTimeParts[0], 10);
      const sessionStartMinutes = parseInt(sessionStartTimeParts[1], 10);
      
      // Initialize times based on session start time
      const sessionStart = new Date();
      sessionStart.setHours(sessionStartHours, sessionStartMinutes, 0, 0);
      const sessionEnd = new Date(sessionStart.getTime() + (session.minBookingDurationMinutes * 60 * 1000));
      
      const calculatedCost = calculateSessionCost(session, session.minBookingDurationMinutes, false);
      
      setFormData({
        startTime: sessionStart,
        endTime: sessionEnd,
        memberNotes: '',
        isGuest: false,
        guestName: '',
        guestEmail: '',
        overrideCost: false,
        overriddenCost: calculatedCost
      });
      
      setSelectedMember(null);
      fetchMembers();
    }
  }, [open, session]);

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const response = await memberProfileAPI.getAllMemberProfiles();
      // Filter to only active members
      const activeMembers = response.data.filter((member: MemberProfile) => 
        member.status === 'active'
      );
      setMemberProfiles(activeMembers);
    } catch (error: any) {
      console.error('Error fetching members:', error);
      setError('Failed to fetch member profiles');
    } finally {
      setLoadingMembers(false);
    }
  };

  const calculateDuration = () => {
    return Math.floor((formData.endTime.getTime() - formData.startTime.getTime()) / (1000 * 60));
  };

  // Helper function to calculate cost based on duration and guest status
  const calculateSessionCost = (session: CoachingSession, durationMinutes: number, isGuest: boolean) => {
    if (isGuest && session.guestRateCost && session.guestRateDuration) {
      return (durationMinutes / session.guestRateDuration) * session.guestRateCost;
    } else if (!isGuest && session.memberRateCost && session.memberRateDuration) {
      return (durationMinutes / session.memberRateDuration) * session.memberRateCost;
    }
    return 0;
  };

  const calculateCost = () => {
    if (!session) return 0;
    
    // If cost override is enabled, return the overridden cost
    if (formData.overrideCost) {
      return formData.overriddenCost;
    }
    
    // Otherwise calculate the cost normally
    const duration = calculateDuration();
    return calculateSessionCost(session, duration, formData.isGuest);
  };

  const handleSubmit = async () => {
    setError(null);
    
    if (!session) return;
    
    // Validation
    if (formData.startTime >= formData.endTime) {
      setError('End time must be after start time');
      return;
    }
    
    const duration = calculateDuration();
    if (duration < session.minBookingDurationMinutes) {
      setError(`Minimum booking duration is ${session.minBookingDurationMinutes} minutes`);
      return;
    }
    
    if (!formData.isGuest && !selectedMember) {
      setError('Please select a member');
      return;
    }
    
    if (formData.isGuest) {
      if (!formData.guestName.trim()) {
        setError('Guest name is required');
        return;
      }
      if (!formData.guestEmail.trim()) {
        setError('Guest email is required');
        return;
      }
    }
    
    try {
      setLoading(true);
      
      const originalCalculatedCost = calculateSessionCost(session, duration, formData.isGuest);
      
      const bookingData: BookCoachingSessionData = {
        startTime: formData.startTime.toTimeString().split(' ')[0],
        endTime: formData.endTime.toTimeString().split(' ')[0],
        durationMinutes: duration,
        memberNotes: formData.memberNotes,
        memberProfileId: formData.isGuest ? undefined : selectedMember?.id,
        isGuest: formData.isGuest,
        guestName: formData.isGuest ? formData.guestName : undefined,
        guestEmail: formData.isGuest ? formData.guestEmail : undefined,
        overrideCost: formData.overrideCost,
        overriddenCost: formData.overrideCost ? formData.overriddenCost : undefined,
        originalCalculatedCost: formData.overrideCost ? originalCalculatedCost : undefined
      };

      await coachingSessionAPI.bookCoachingSession(session.id, bookingData);
      
      onSuccess?.('Coaching session booked successfully');
      onClose();
    } catch (error: any) {
      console.error('Error booking session:', error);
      
      // Extract the error message from the backend response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to book coaching session';
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTimeChange = (newStartTime: Date | null) => {
    if (!newStartTime || !session) return;
    
    const newEndTime = new Date(newStartTime.getTime() + (session.minBookingDurationMinutes * 60 * 1000));
    
    // If cost override is disabled, update the overriddenCost with recalculated value
    if (!formData.overrideCost && session) {
      const newDuration = Math.floor((newEndTime.getTime() - newStartTime.getTime()) / (1000 * 60));
      const recalculatedCost = calculateSessionCost(session, newDuration, formData.isGuest);
      setFormData({
        ...formData,
        startTime: newStartTime,
        endTime: newEndTime,
        overriddenCost: recalculatedCost
      });
    } else {
      setFormData({
        ...formData,
        startTime: newStartTime,
        endTime: newEndTime
      });
    }
  };

  // Check if user has permission to override cost
  const canOverrideCost = user && (
    user.role === UserRole.SUPER_ADMIN ||
    user.role === UserRole.ADMIN ||
    user.role === UserRole.EMPLOYEE ||
    user.role === UserRole.COACH
  );

  if (!session) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} disableEscapeKeyDown={true} maxWidth="sm" fullWidth>
        <DialogTitle>
          Book Coaching Session - {session.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Session Info */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Coach:</strong> {session.coachName} • <strong>Venues:</strong> {session.venues && session.venues.length > 0 ? `${session.venues.length} venue(s)` : 'Not specified'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Date:</strong> {parseDateOnlyString(session.sessionDate).toDateString()} • 
                  <strong> Available:</strong> {formatTimeString(session.startTime)} - {formatTimeString(session.endTime)}
                </Typography>
              </Box>
            </Grid>

            {/* Guest Toggle */}
            {session.allowGuestBookings && (
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isGuest}
                      onChange={(e) => {
                        setFormData({ 
                          ...formData, 
                          isGuest: e.target.checked
                        });
                        if (e.target.checked) {
                          setSelectedMember(null);
                        }
                      }}
                    />
                  }
                  label="Book for Guest"
                />
              </Grid>
            )}

            {/* Member Selection */}
            {!formData.isGuest && (
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={memberProfiles}
                  value={selectedMember}
                  onChange={(event, newValue) => setSelectedMember(newValue)}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                  getOptionKey={(option) => option.id}
                  loading={loadingMembers}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body1">
                          {option.firstName} {option.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.email} • {option.membershipTier?.name || 'No Tier'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Member"
                      placeholder="Search members..."
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingMembers ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  noOptionsText={loadingMembers ? "Loading members..." : "No members found"}
                />
              </Grid>
            )}

            {/* Guest Information */}
            {formData.isGuest && (
              <>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Guest Name"
                    value={formData.guestName}
                    onChange={(e) => setFormData({ ...formData, guestName: e.target.value })}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Guest Email"
                    type="email"
                    value={formData.guestEmail}
                    onChange={(e) => setFormData({ ...formData, guestEmail: e.target.value })}
                    fullWidth
                    required
                  />
                </Grid>
              </>
            )}

            {/* Time Selection */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TimePicker
                label="Start Time"
                value={formData.startTime}
                onChange={handleStartTimeChange}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TimePicker
                label="End Time"
                value={formData.endTime}
                onChange={(time) => time && setFormData({ ...formData, endTime: time })}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </Grid>

            {/* Duration and Cost Display */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Duration:</strong> {calculateDuration()} minutes
                </Typography>
                {(session.memberRateCost || session.guestRateCost) && (
                  <Typography variant="body2">
                    <strong>{formData.overrideCost ? 'Original Cost:' : 'Cost:'}</strong> ${calculateSessionCost(session, calculateDuration(), formData.isGuest).toFixed(2)}
                    {formData.isGuest && session.guestRateCost && session.guestRateDuration && 
                      ` (Guest rate: $${session.guestRateCost}/${session.guestRateDuration}min)`}
                    {!formData.isGuest && session.memberRateCost && session.memberRateDuration && 
                      ` (Member rate: $${session.memberRateCost}/${session.memberRateDuration}min)`}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Cost Override - Only for privileged roles */}
            {canOverrideCost && (session.memberRateCost || session.guestRateCost) && (
              <>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.overrideCost}
                        onChange={(e) => {
                          const isOverriding = e.target.checked;
                          const currentCalculatedCost = calculateSessionCost(
                            session,
                            calculateDuration(),
                            formData.isGuest
                          );
                          setFormData({ 
                            ...formData, 
                            overrideCost: isOverriding,
                            overriddenCost: isOverriding ? formData.overriddenCost : currentCalculatedCost
                          });
                        }}
                      />
                    }
                    label="Override Cost"
                  />
                </Grid>
                {formData.overrideCost && (
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="New Cost"
                      type="number"
                      value={formData.overriddenCost}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        setFormData({ 
                          ...formData, 
                          overriddenCost: isNaN(value) ? 0 : value
                        });
                      }}
                      fullWidth
                      required
                      inputProps={{ min: 0, step: 0.01 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      helperText="Enter a cost for this session."
                    />
                  </Grid>
                )}
              </>
            )}

            {/* Notes */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Notes"
                value={formData.memberNotes}
                onChange={(e) => setFormData({ ...formData, memberNotes: e.target.value })}
                fullWidth
                multiline
                rows={3}
                placeholder="Any special requests or notes for the coach"
              />
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Booking...' : 'Book Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default BookCoachingSessionDialog;
